import { ChangeDetectionStrategy, Component, DestroyRef, HostListener, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators, FormGroup } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { LoginService } from '../login.service';
import { ToastService } from '../toast/toast.service';
import { APP_BRAND } from '../branding/environment.config';
import { AuthDialogService } from '../auth-dialog.service';
import { finalize } from 'rxjs';
import { SessionService } from '../session.service';

@Component({
  selector: 'app-login-modal',
  standalone: true,
  imports: [A11yModule, CommonModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './login-modal.html',
  styleUrl: './login-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginModal {
  private readonly fb = inject(FormBuilder);
  private readonly authDialog = inject(AuthDialogService);
  private readonly loginService = inject(LoginService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly brand = APP_BRAND;
  protected readonly session = inject(SessionService);
  protected readonly isSubmitting = signal(false);

  readonly form: FormGroup = this.fb.nonNullable.group({
    username: ['', Validators.required],
    password: ['', Validators.required]
  });

  submit(): void {
    if (this.form.invalid || this.isSubmitting()) {
      return;
    }

    const { username, password } = this.form.getRawValue();
    this.isSubmitting.set(true);

    this.loginService
      .login({
        AccountNr: username,
        Password: password,
        RememberMe: true,
        Device: null
      })
      .pipe(
        finalize(() => this.isSubmitting.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => this.close(),
        error: (err) => {
          this.toastService.showError('auth.loginFailed');
          console.error('Login failed', err);
        }
      });
  }

  ignoreAuxiliaryAction(event: Event): void {
    event.preventDefault();
  }

  close(): void {
    this.authDialog.closeLogin();
  }

  @HostListener('document:keydown.escape', ['$event'])
  protected onEscape(event: Event): void {
    event.preventDefault();
    this.close();
  }
}
